import * as React from 'react'
import { hexColorSchema } from '@lib/validators/tenant.validator'
import { cn } from '@/lib/utils'
import { Input } from './input'

export interface ColorInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  id: string
  /** Free text while typing, or '' for none. A finished value matches `hexColorSchema`. */
  value: string
  onChange: (value: string) => void
}

/**
 * A hex color field: type it, or click the swatch for the native picker. The
 * swatch shows the value once `hexColorSchema` accepts it and stays empty
 * otherwise. Colors here are data (a tenant's brand), not theme, so the swatch
 * is styled inline.
 */
export function ColorInput({ id, value, onChange, className, disabled, ...props }: ColorInputProps) {
  const parsed = hexColorSchema.safeParse(value)
  const swatch = parsed.success ? parsed.data : null
  const pickerRef = React.useRef<HTMLInputElement>(null)

  // The native picker can't hold "no color", so it isn't controlled: it follows
  // the text only once that is a full hex color.
  React.useEffect(() => {
    if (swatch !== null && pickerRef.current) pickerRef.current.value = swatch
  }, [swatch])

  return (
    <div className={cn('relative', className)}>
      <label
        className={cn(
          'absolute top-1/2 left-1.5 size-5 -translate-y-1/2 cursor-pointer overflow-hidden rounded-sm border border-border-strong',
          swatch === null ? 'bg-subtle' : null,
          disabled ? 'pointer-events-none opacity-60' : null,
        )}
        style={swatch !== null ? { backgroundColor: swatch } : undefined}
      >
        <span className="sr-only">Pick a color</span>
        <input
          ref={pickerRef}
          type="color"
          tabIndex={-1}
          disabled={disabled}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
      <Input
        id={id}
        className="pl-8 font-mono"
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...props}
      />
    </div>
  )
}
